<template>
  <div class="m2m-schedule-page">
    <h1 class="title">Расписание приема поручений M2M</h1>

    <div v-if="loading" class="loader"></div>

    <template v-else>
      <section class="schedule-section">
        <h2>Базовое расписание</h2>
        <p class="section-description">
          Понедельник–пятница считаются рабочими днями, суббота и воскресенье — выходными.
          Указанное время применяется по Москве.
        </p>

        <div class="schedule-fields">
          <b-field label="Начало приема">
            <b-input v-model="schedule.startTime" type="time" step="60" />
          </b-field>
          <b-field label="Окончание приема">
            <b-input v-model="schedule.endTime" type="time" step="60" />
          </b-field>
          <div class="timezone-field">
            <span class="label">Часовой пояс</span>
            <span class="timezone-value">Москва (Europe/Moscow)</span>
          </div>
        </div>

        <button
          class="button is-info"
          :disabled="savingSchedule"
          @click="saveSchedule"
        >
          {{ savingSchedule ? 'Сохранение...' : 'Сохранить расписание' }}
        </button>
      </section>

      <section class="schedule-section">
        <h2>Добавить исключение</h2>
        <p class="section-description">
          Исключение имеет приоритет над базовым расписанием и действует только в выбранную дату.
        </p>

        <div class="exception-form">
          <b-field label="Дата">
            <el-date-picker
              v-model="newException.date"
              type="date"
              :editable="false"
              placeholder="Выберите дату"
            />
          </b-field>

          <div class="day-type-field">
            <span class="label">Тип исключения</span>
            <b-radio-group v-model="newException.isWorking" class="day-type-options">
              <b-radio-button :value="false" type="is-info">Нерабочий день</b-radio-button>
              <b-radio-button :value="true" type="is-info">Рабочий день</b-radio-button>
            </b-radio-group>
          </div>

          <div class="switch-field">
            <b-switch v-model="newException.useCustomTime">
              Использовать индивидуальные часы
            </b-switch>
            <p v-if="!newException.useCustomTime" class="field-hint">
              <template v-if="newException.isWorking">
                Действует базовое расписание:
                {{ schedule.startTime }}–{{ schedule.endTime }}.
              </template>
              <template v-else>
                Прием будет закрыт на весь день.
              </template>
            </p>
          </div>

          <div v-if="newException.useCustomTime" class="custom-time-fields">
            <b-field label="Начало">
              <b-input v-model="newException.startTime" type="time" step="60" />
            </b-field>
            <b-field label="Окончание">
              <b-input v-model="newException.endTime" type="time" step="60" />
            </b-field>
          </div>

          <b-field label="Комментарий">
            <b-input
              v-model="newException.comment"
              placeholder="Например, праздничный или перенесенный рабочий день"
            />
          </b-field>
        </div>

        <button
          class="button is-info add-exception-button"
          :disabled="savingNewException"
          @click="createException"
        >
          {{ savingNewException ? 'Добавление...' : 'Добавить исключение' }}
        </button>
      </section>

      <section class="schedule-section exceptions-section">
        <h2>Календарные исключения</h2>

        <div class="exceptions-filter">
          <span class="label">Тип исключения</span>
          <b-radio-group v-model="exceptionTypeFilter" class="day-type-options">
            <b-radio-button value="all" type="is-info">Все</b-radio-button>
            <b-radio-button value="nonWorking" type="is-info">Нерабочие дни</b-radio-button>
            <b-radio-button value="working" type="is-info">Рабочие дни</b-radio-button>
          </b-radio-group>
        </div>

        <p v-if="!exceptions.length" class="empty-message">Исключений пока нет.</p>

        <p v-else-if="!filteredExceptions.length" class="empty-message">
          Исключений выбранного типа нет.
        </p>

        <div v-else class="table-wrapper">
          <table class="table is-fullwidth exceptions-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Тип</th>
                <th>Время</th>
                <th>Комментарий</th>
                <th class="actions-column">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="exception in filteredExceptions" :key="exception.id">
                <td>{{ formatDate(exception.date) }}</td>
                <td>{{ exception.isWorking ? 'Рабочий день' : 'Нерабочий день' }}</td>
                <td class="time-column">{{ formatExceptionHours(exception) }}</td>
                <td>{{ exception.comment || '—' }}</td>
                <td class="actions-column">
                  <button class="icon-button" title="Редактировать исключение" @click="openEditException(exception)">
                    <img src="/static/img/icons/edit.svg" alt="Редактировать" />
                  </button>
                  <button class="icon-button" title="Удалить исключение" @click="confirmDeleteException(exception)">
                    <img src="/static/img/icons/delete.svg" alt="Удалить" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div v-if="editingException" class="modal is-active">
        <div class="modal-background" @click="closeEditException"></div>
        <div class="modal-card exception-modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Редактировать исключение</p>
            <button
              class="delete"
              aria-label="Закрыть"
              title="Закрыть"
              @click="closeEditException"
            ></button>
          </header>

          <section class="modal-card-body">
            <b-field label="Дата">
              <el-date-picker
                v-model="editingException.date"
                type="date"
                :editable="false"
                placeholder="Выберите дату"
              />
            </b-field>

            <div class="day-type-field">
              <span class="label">Тип исключения</span>
              <b-radio-group v-model="editingException.isWorking" class="day-type-options">
                <b-radio-button :value="false" type="is-info">Нерабочий день</b-radio-button>
                <b-radio-button :value="true" type="is-info">Рабочий день</b-radio-button>
              </b-radio-group>
            </div>

            <div class="switch-field">
              <b-switch v-model="editingException.useCustomTime">
                Использовать индивидуальные часы
              </b-switch>
              <p v-if="!editingException.useCustomTime" class="field-hint">
                <template v-if="editingException.isWorking">
                  Действует базовое расписание:
                  {{ schedule.startTime }}–{{ schedule.endTime }}.
                </template>
                <template v-else>
                  Прием будет закрыт на весь день.
                </template>
              </p>
            </div>

            <div v-if="editingException.useCustomTime" class="custom-time-fields">
              <b-field label="Начало">
                <b-input v-model="editingException.startTime" type="time" step="60" />
              </b-field>
              <b-field label="Окончание">
                <b-input v-model="editingException.endTime" type="time" step="60" />
              </b-field>
            </div>

            <b-field label="Комментарий">
              <b-input
                v-model="editingException.comment"
                placeholder="Например, праздничный или перенесенный рабочий день"
              />
            </b-field>
          </section>

          <footer class="modal-card-foot">
            <button class="button" :disabled="savingException" @click="closeEditException">
              Отмена
            </button>
            <button
              class="button is-info"
              :disabled="savingException"
              @click="updateException"
            >
              {{ savingException ? 'Сохранение...' : 'Сохранить' }}
            </button>
          </footer>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'M2MSchedule',
  data () {
    return {
      loading: true,
      savingSchedule: false,
      savingNewException: false,
      savingException: false,
      editingException: null,
      exceptionTypeFilter: 'all',
      schedule: {
        startTime: '10:00',
        endTime: '16:00',
        timezone: 'Europe/Moscow'
      },
      exceptions: [],
      newException: this.createEmptyException()
    }
  },
  created () {
    this.loadSchedule()
  },
  computed: {
    filteredExceptions () {
      if (this.exceptionTypeFilter === 'working') {
        return this.exceptions.filter(exception => exception.isWorking)
      }
      if (this.exceptionTypeFilter === 'nonWorking') {
        return this.exceptions.filter(exception => !exception.isWorking)
      }
      return this.exceptions
    }
  },
  methods: {
    createEmptyException () {
      return {
        date: null,
        isWorking: false,
        useCustomTime: false,
        startTime: '10:00',
        endTime: '16:00',
        comment: ''
      }
    },
    normalizeTime (value) {
      return value ? String(value).slice(0, 5) : ''
    },
    parseDate (value) {
      if (!value) {
        return null
      }
      if (value instanceof Date) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate())
      }
      const stringValue = String(value)
      const dateValue = /^\d{4}-\d{2}-\d{2}$/.test(stringValue)
        ? stringValue
        : this.formatLocalDate(new Date(stringValue))
      const parts = dateValue.split('-').map(Number)
      return new Date(parts[0], parts[1] - 1, parts[2])
    },
    formatLocalDate (date) {
      const year = date.getFullYear()
      const month = `0${date.getMonth() + 1}`.slice(-2)
      const day = `0${date.getDate()}`.slice(-2)
      return `${year}-${month}-${day}`
    },
    formatDate (value) {
      if (!value) {
        return ''
      }
      const date = value instanceof Date ? value : this.parseDate(value)
      return this.formatLocalDate(date)
    },
    normalizeException (exception) {
      const hasExplicitCustomTime = typeof exception.useCustomTime === 'boolean'
      const useCustomTime = hasExplicitCustomTime
        ? exception.useCustomTime
        : Boolean(exception.startTime && exception.endTime)

      return {
        id: exception.id,
        date: this.parseDate(exception.date),
        isWorking: Boolean(exception.isWorking),
        useCustomTime,
        startTime: this.normalizeTime(exception.startTime) || this.schedule.startTime,
        endTime: this.normalizeTime(exception.endTime) || this.schedule.endTime,
        comment: exception.comment || ''
      }
    },
    serializeException (exception) {
      const useCustomTime = Boolean(exception.useCustomTime)
      return {
        date: this.formatDate(exception.date),
        isWorking: Boolean(exception.isWorking),
        useCustomTime,
        startTime: useCustomTime ? exception.startTime : null,
        endTime: useCustomTime ? exception.endTime : null,
        comment: exception.comment || null
      }
    },
    formatExceptionHours (exception) {
      if (exception.useCustomTime) {
        return `${exception.startTime}–${exception.endTime}`
      }
      if (exception.isWorking) {
        return `По базовому расписанию (${this.schedule.startTime}–${this.schedule.endTime})`
      }
      return 'Прием закрыт весь день'
    },
    getErrorMessage (error) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : 'Не удалось выполнить операцию'
    },
    showError (error) {
      this.$toast.open({ message: this.getErrorMessage(error) })
    },
    loadSchedule () {
      this.loading = true
      this.axios.get('/m2m-schedule')
        .then(({ data }) => {
          this.schedule = {
            startTime: this.normalizeTime(data.schedule.startTime),
            endTime: this.normalizeTime(data.schedule.endTime),
            timezone: data.schedule.timezone
          }
          this.exceptions = data.exceptions.map(exception => this.normalizeException(exception))
        })
        .catch(this.showError)
        .then(() => {
          this.loading = false
        })
    },
    saveSchedule () {
      this.savingSchedule = true
      this.axios.put('/m2m-schedule', { schedule: this.schedule })
        .then(({ data }) => {
          this.schedule.startTime = this.normalizeTime(data.startTime)
          this.schedule.endTime = this.normalizeTime(data.endTime)
          this.$toast.open({ message: 'Базовое расписание сохранено' })
        })
        .catch(this.showError)
        .then(() => {
          this.savingSchedule = false
        })
    },
    createException () {
      this.savingNewException = true
      this.axios.post('/m2m-schedule/exceptions', {
        exception: this.serializeException(this.newException)
      })
        .then(({ data }) => {
          this.exceptions.push(this.normalizeException(data))
          this.exceptions.sort((a, b) => a.date - b.date)
          this.newException = this.createEmptyException()
          this.$toast.open({ message: 'Исключение добавлено' })
        })
        .catch(this.showError)
        .then(() => {
          this.savingNewException = false
        })
    },
    openEditException (exception) {
      this.editingException = this.normalizeException(exception)
    },
    closeEditException () {
      if (!this.savingException) {
        this.editingException = null
      }
    },
    updateException () {
      const exception = this.editingException
      if (!exception) {
        return
      }

      this.savingException = true
      this.axios.put(`/m2m-schedule/exceptions/${exception.id}`, {
        exception: this.serializeException(exception)
      })
        .then(({ data }) => {
          const updatedException = this.normalizeException(data)
          const index = this.exceptions.findIndex(item => item.id === updatedException.id)
          if (index !== -1) {
            this.exceptions.splice(index, 1, updatedException)
          }
          this.exceptions.sort((a, b) => a.date - b.date)
          this.editingException = null
          this.$toast.open({ message: 'Исключение сохранено' })
        })
        .catch(this.showError)
        .then(() => {
          this.savingException = false
        })
    },
    confirmDeleteException (exception) {
      this.$dialog.confirm({
        message: 'Удалить календарное исключение?',
        onConfirm: () => this.deleteException(exception)
      })
    },
    deleteException (exception) {
      this.axios.delete(`/m2m-schedule/exceptions/${exception.id}`)
        .then(() => {
          this.exceptions = this.exceptions.filter(item => item.id !== exception.id)
          this.$toast.open({ message: 'Исключение удалено' })
        })
        .catch(this.showError)
    }
  }
}
</script>

<style scoped>
.m2m-schedule-page {
  max-width: 1240px;
  padding: 24px 32px 48px;
}

.title {
  margin-bottom: 24px;
  font-size: 1.6rem;
}

.schedule-section {
  padding: 24px 0 30px;
  border-bottom: 1px solid #dbdbdb;
}

.schedule-section h2 {
  margin-bottom: 8px;
  font-size: 1.25rem;
  font-weight: 600;
}

.section-description {
  max-width: 820px;
  margin-bottom: 20px;
  color: #666;
}

.schedule-fields {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.custom-time-fields {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.schedule-fields .field,
.custom-time-fields .field {
  width: 190px;
}

.timezone-field {
  display: flex;
  flex-direction: column;
  min-height: 61px;
}

.timezone-value {
  display: flex;
  align-items: center;
  min-height: 36px;
}

.exception-form {
  max-width: 760px;
}

.switch-field {
  margin-bottom: 18px;
}

.switch-field .label,
.day-type-field .label {
  display: block;
  margin-bottom: 8px;
}

.field-hint {
  margin-top: 8px;
  color: #777;
  font-size: 13px;
}

.day-type-field {
  margin-bottom: 18px;
}

.day-type-options {
  margin-bottom: 0;
}

.add-exception-button {
  margin-top: 18px;
}

.exceptions-section {
  border-bottom: 0;
}

.exceptions-filter {
  margin: 20px 0 24px;
}

.exceptions-filter .label {
  display: block;
  margin-bottom: 8px;
}

.table-wrapper {
  overflow-x: auto;
}

.exceptions-table {
  min-width: 900px;
}

.exceptions-table th,
.exceptions-table td {
  vertical-align: middle;
}

.time-column {
  min-width: 230px;
}

.base-time-caption,
.empty-message {
  color: #777;
}

.actions-column {
  width: 100px;
  white-space: nowrap;
}

.icon-button {
  width: 36px;
  height: 36px;
  padding: 2px;
  border: 0;
  background: transparent;
  cursor: pointer;
  vertical-align: middle;
}

.icon-button + .icon-button {
  margin-left: 8px;
}

.icon-button img {
  display: block;
  width: 30px;
  height: 30px;
}

.exception-modal-card {
  width: 640px;
  max-width: calc(100vw - 40px);
}

.exception-modal-card .modal-card-body {
  overflow: visible;
}

.exception-modal-card .el-date-editor {
  width: 190px;
}

.exception-modal-card .modal-card-foot {
  justify-content: flex-end;
}

.loader {
  margin: 60px auto;
  width: 3rem !important;
  height: 3rem !important;
}

@media (max-width: 900px) {
  .m2m-schedule-page {
    padding: 20px;
  }

  .schedule-fields,
  .custom-time-fields {
    align-items: stretch;
    flex-direction: column;
    gap: 0;
  }
}
</style>



// WEBPACK FOOTER //
// Index.vue?a95e50d8